import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, Plus, TrendingUp, Sparkles, Bookmark,
  SlidersHorizontal, X, ChevronDown, Flame, Users, ArrowLeft
} from 'lucide-react';
import { RecipePostCard } from '../components/food-community/RecipePostCard';
import {
  foodCommunityService,
  RecipePost,
  RecipeUser,
  DateFilter,
} from '../services/foodCommunityService';
import { useAuth } from '../contexts/AuthContext';

type Tab = 'feed' | 'trending' | 'for-you' | 'collections';

const DATE_FILTERS: { value: DateFilter; label: string }[] = [
  { value: 'all', label: 'Toate zilele' },
  { value: 'today', label: 'Azi' },
  { value: 'yesterday', label: 'Ieri' },
  { value: 'week', label: 'Saptamana asta' },
];

const MEAL_TYPES = [
  { value: '', label: 'Toate' },
  { value: 'breakfast', label: 'Mic dejun' },
  { value: 'lunch', label: 'Pranz' },
  { value: 'dinner', label: 'Cina' },
  { value: 'snack', label: 'Gustare' },
];

const ALL_TAGS = [
  { value: 'HIGH_PROTEIN', label: '#highprotein' },
  { value: 'LOW_CALORIE', label: '#lowcalorie' },
  { value: 'LOW_CARB', label: '#lowcarb' },
  { value: 'VEGAN', label: '#vegan' },
  { value: 'VEGETARIAN', label: '#vegetarian' },
  { value: 'GLUTEN_FREE', label: '#faragluten' },
  { value: 'ANTI_INFLAMMATORY', label: '#antiinflamator' },
  { value: 'WEIGHT_LOSS', label: '#slabit' },
  { value: 'MUSCLE_GAIN', label: '#masamusculara' },
  { value: 'KETO', label: '#keto' },
  { value: 'BALANCED', label: '#echilibrat' },
];

const StoriesBar: React.FC<{ users: RecipeUser[]; onAddStory: () => void }> = ({ users, onAddStory }) => {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-3 px-4 py-3 overflow-x-auto scrollbar-hide">
      {/* Add Story */}
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <button
          onClick={onAddStory}
          className="w-14 h-14 rounded-full border-2 border-dashed border-green-400 flex items-center justify-center bg-green-50 hover:bg-green-100 transition-colors"
        >
          <Plus size={20} className="text-green-600" />
        </button>
        <span className="text-xs text-gray-500 font-medium">Adauga</span>
      </div>

      {/* User Stories */}
      {users.map(user => {
        const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        return (
          <div
            key={user.id}
            className="flex flex-col items-center gap-1 flex-shrink-0 cursor-pointer"
            onClick={() => navigate(`/food-community/user/${user.id}`)}
          >
            <div className="w-14 h-14 rounded-full ring-2 ring-green-400 ring-offset-2 overflow-hidden">
              {user.profilePhotoUrl ? (
                <img src={user.profilePhotoUrl} alt={name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white font-semibold text-sm">
                  {initials}
                </div>
              )}
            </div>
            <span className="text-xs text-gray-600 font-medium max-w-[56px] truncate">{name.split(' ')[0]}</span>
          </div>
        );
      })}
    </div>
  );
};

const FilterPanel: React.FC<{
  dateFilter: DateFilter;
  selectedTags: string[];
  mealType: string;
  onDateChange: (v: DateFilter) => void;
  onTagToggle: (v: string) => void;
  onMealTypeChange: (v: string) => void;
  onClose: () => void;
  onReset: () => void;
}> = ({ dateFilter, selectedTags, mealType, onDateChange, onTagToggle, onMealTypeChange, onClose, onReset }) => (
  <div className="bg-white border-b border-gray-100 px-4 py-4 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-gray-800 text-sm">Filtre</h3>
      <div className="flex items-center gap-3">
        <button onClick={onReset} className="text-xs text-gray-400 hover:text-gray-600">Reseteaza</button>
        <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
      </div>
    </div>

    {/* Date Filter */}
    <div className="mb-3">
      <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Perioada</p>
      <div className="flex flex-wrap gap-2">
        {DATE_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => onDateChange(f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              dateFilter === f.value
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
    </div>

    {/* Meal Type */}
    <div className="mb-3">
      <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Tip masa</p>
      <div className="flex flex-wrap gap-2">
        {MEAL_TYPES.map(m => (
          <button
            key={m.value}
            onClick={() => onMealTypeChange(m.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              mealType === m.value
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
    </div>

    {/* Tags */}
    <div>
      <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Etichete</p>
      <div className="flex flex-wrap gap-2">
        {ALL_TAGS.map(t => (
          <button
            key={t.value}
            onClick={() => onTagToggle(t.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              selectedTags.includes(t.value)
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const FoodCommunityPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.id || (user as any)?.userId;
  const [activeTab, setActiveTab] = useState<Tab>('feed');
  const [posts, setPosts] = useState<RecipePost[]>([]);
  const [stories, setStories] = useState<RecipeUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [mealType, setMealType] = useState('');
  const loaderRef = useRef<HTMLDivElement>(null);

  const activeFiltersCount = (dateFilter !== 'all' ? 1 : 0) + selectedTags.length + (mealType ? 1 : 0);

  const loadPosts = useCallback(async (reset = false) => {
    const currentPage = reset ? 1 : page;
    if (!reset && !hasMore) return;

    if (reset) {
      setLoading(true);
      setPosts([]);
    } else {
      setLoadingMore(true);
    }

    try {
      let result: { posts: RecipePost[]; pagination?: any };

      if (activeTab === 'feed') {
        result = await foodCommunityService.getFeed({
          page: currentPage,
          limit: 15,
          dateFilter,
          tags: selectedTags.length > 0 ? selectedTags : undefined,
          mealType: mealType || undefined,
        });
      } else if (activeTab === 'trending') {
        result = await foodCommunityService.getTrending(30);
      } else {
        result = await foodCommunityService.getForYou(30);
      }

      if (reset) {
        setPosts(result.posts);
      } else {
        setPosts(prev => [...prev, ...result.posts]);
      }

      if (result.pagination) {
        setHasMore(result.pagination.hasMore);
        setPage(currentPage + 1);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeTab, page, hasMore, dateFilter, selectedTags, mealType]);

  // Load stories
  useEffect(() => {
    foodCommunityService.getStoriesBar()
      .then(r => setStories(r.users || []))
      .catch(() => {});
  }, []);

  // Reload on tab/filter change
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    loadPosts(true);
  }, [activeTab, dateFilter, selectedTags, mealType]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadPosts(false);
        }
      },
      { threshold: 0.1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadPosts]);

  const handlePostUpdate = (updated: RecipePost) => {
    setPosts(prev => prev.map(p => p.id === updated.id ? updated : p));
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleResetFilters = () => {
    setDateFilter('all');
    setSelectedTags([]);
    setMealType('');
  };

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'feed', label: 'Feed', icon: <Flame size={15} /> },
    { id: 'trending', label: 'Trending', icon: <TrendingUp size={15} /> },
    { id: 'for-you', label: 'Pentru tine', icon: <Sparkles size={15} /> },
    { id: 'collections', label: 'Colectiile mele', icon: <Bookmark size={15} /> },
  ];

  if (activeTab === 'collections') {
    navigate('/food-community/collections');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={18} className="text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Flame size={16} className="text-white" />
              </div>
              <span className="font-bold text-gray-900 text-lg">Food Community</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/food-community/search')}
              className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <Search size={17} className="text-gray-600" />
            </button>
            <button
              onClick={() => navigate('/notifications')}
              className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors relative"
            >
              <Bell size={17} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-2xl mx-auto px-4 flex gap-1 overflow-x-auto scrollbar-hide pb-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.id === 'collections') {
                  navigate('/food-community/collections');
                } else {
                  setActiveTab(tab.id);
                }
              }}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Stories Bar (only on Feed tab) */}
        {activeTab === 'feed' && (
          <div className="bg-white border-b border-gray-100">
            <StoriesBar users={stories} onAddStory={() => navigate('/food-community/create')} />
          </div>
        )}

        {/* Filter Bar (only on Feed tab) */}
        {activeTab === 'feed' && (
          <div className="bg-white border-b border-gray-100 px-4 py-2 flex items-center gap-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${
                activeFiltersCount > 0
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal size={13} />
              Filtre
              {activeFiltersCount > 0 && (
                <span className="bg-white text-green-600 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold">
                  {activeFiltersCount}
                </span>
              )}
              <ChevronDown size={12} className={showFilters ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </button>

            {/* Quick date filters */}
            {DATE_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setDateFilter(f.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition-colors ${
                  dateFilter === f.value
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Filter Panel */}
        {showFilters && activeTab === 'feed' && (
          <FilterPanel
            dateFilter={dateFilter}
            selectedTags={selectedTags}
            mealType={mealType}
            onDateChange={setDateFilter}
            onTagToggle={handleTagToggle}
            onMealTypeChange={setMealType}
            onClose={() => setShowFilters(false)}
            onReset={handleResetFilters}
          />
        )}

        {/* Tab Header for Trending/ForYou */}
        {activeTab === 'trending' && (
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-green-600" />
              <h2 className="font-bold text-gray-900">Top Retete Saptamana Asta</h2>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">Cele mai apreciate retete din comunitate</p>
          </div>
        )}

        {activeTab === 'for-you' && (
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-green-600" />
              <h2 className="font-bold text-gray-900">Retete pentru tine</h2>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">Recomandate de AI bazat pe obiectivele tale</p>
          </div>
        )}

        {/* Posts */}
        <div className="px-4 pt-3 pb-24">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
                  <div className="flex items-center gap-3 p-4">
                    <div className="w-10 h-10 rounded-full bg-gray-200" />
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 rounded w-32 mb-1.5" />
                      <div className="h-2.5 bg-gray-100 rounded w-20" />
                    </div>
                  </div>
                  <div className="h-52 bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-full" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Users size={28} className="text-gray-300" />
              </div>
              <h3 className="font-semibold text-gray-700 mb-1">Nicio reteta gasita</h3>
              <p className="text-sm text-gray-400 mb-4">
                {activeFiltersCount > 0
                  ? 'Incearca sa schimbi filtrele'
                  : 'Fii primul care posteaza o reteta!'}
              </p>
              {activeFiltersCount > 0 ? (
                <button
                  onClick={handleResetFilters}
                  className="text-sm text-green-600 font-medium hover:text-green-700"
                >
                  Reseteaza filtrele
                </button>
              ) : (
                <button
                  onClick={() => navigate('/food-community/create')}
                  className="bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors"
                >
                  Adauga prima reteta
                </button>
              )}
            </div>
          ) : (
            <>
              {posts.map(post => (
                <RecipePostCard
                  key={post.id}
                  post={post}
                  onUpdate={handlePostUpdate}
                  onDelete={(postId) => setPosts(prev => prev.filter(p => p.id !== postId))}
                  currentUserId={currentUserId}
                />
              ))}

              {/* Infinite scroll loader */}
              <div ref={loaderRef} className="py-4 flex justify-center">
                {loadingMore && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                    Se incarca...
                  </div>
                )}
                {!hasMore && posts.length > 0 && (
                  <p className="text-xs text-gray-300">Ai vazut toate retetele</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* FAB - Create Post */}
      <button
        onClick={() => navigate('/food-community/create')}
        className="fixed bottom-20 right-4 w-14 h-14 bg-green-600 hover:bg-green-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95 z-20"
        style={{ boxShadow: '0 4px 20px rgba(22, 163, 74, 0.4)' }}
      >
        <Plus size={24} />
      </button>
    </div>
  );
};

export default FoodCommunityPage;
